import { uuid } from './uuid.helpers';

export type BrowserDeviceInfo = {
  device: string;
  deviceOs: string;
  deviceName: string;
  browser: string;
  browserVersion: string;
  browserId: string;
};

type OsInfo = { deviceName: string; deviceOs: string };

type BrowserInfo = { browser: string; browserVersion: string };

type DeviceInfo = { device: string };

export function getBrowserDeviceInfo(): BrowserDeviceInfo {
  const ua: string = getUserAgent();
  const os: OsInfo = parseOs(ua);
  const device: DeviceInfo = parseDeviceType(ua, getIsTouchDevice());
  const browser: BrowserInfo = parseBrowser(ua);
  const webViewOverride: BrowserInfo | null = detectIOSWebView(ua);
  const finalBrowser: string = webViewOverride?.browser ?? browser.browser;
  const finalBrowserVersion: string = webViewOverride?.browserVersion ?? browser.browserVersion;

  return {
    device: safe(device.device),
    deviceOs: safe(os.deviceOs),
    deviceName: safe(os.deviceName),
    browser: safe(finalBrowser),
    browserVersion: safe(finalBrowserVersion),
    browserId: getOrCreateBrowserId(),
  };
}

function getUserAgent(): string {
  return typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
}

function getIsTouchDevice(): boolean {
  return typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 0;
}

function pick(ua: string, re: RegExp): string {
  return ua.match(re)?.[1] ?? '';
}

function normalizeVer(v: string): string {
  return v.replace(/_/g, '.');
}

function safe(v: string, fallback = 'unknown'): string {
  return v && v.trim().length ? v : fallback;
}

function parseOs(ua: string): OsInfo {
  const iosVer: string = pick(ua, /OS ([0-9_]+) like Mac OS X/);
  if (/iPhone|iPad|iPod/i.test(ua) && iosVer) {
    return { deviceName: 'iOS', deviceOs: normalizeVer(iosVer) };
  }

  const androidVer: string = pick(ua, /Android ([0-9.]+)/);
  if (/Android/i.test(ua) && androidVer) {
    return { deviceName: 'Android', deviceOs: androidVer };
  }

  const winNt: string = pick(ua, /Windows NT ([0-9.]+)/);
  if (/Windows NT/i.test(ua) && winNt) {
    return { deviceName: 'Windows', deviceOs: mapWindowsNtToMarketing(winNt) };
  }

  const macVer: string = pick(ua, /Mac OS X ([0-9_]+)/);
  if (/Macintosh/i.test(ua) && macVer) {
    return { deviceName: 'macOS', deviceOs: normalizeVer(macVer) };
  }

  if (/Linux/i.test(ua) && !/Android/i.test(ua)) {
    return { deviceName: 'Linux', deviceOs: 'unknown' };
  }

  return { deviceName: 'unknown', deviceOs: 'unknown' };
}

function mapWindowsNtToMarketing(nt: string): string {
  const map: Record<string, string> = {
    '10.0': '10/11',
    '6.3': '8.1',
    '6.2': '8',
    '6.1': '7',
    '6.0': 'Vista',
    '5.2': 'XP',
    '5.1': 'XP',
  };
  return map[nt] ?? nt;
}

function parseDeviceType(ua: string, isTouch: boolean): DeviceInfo {
  if (/iPad/i.test(ua)) return { device: 'tablet' };
  if (/iPhone|iPod/i.test(ua)) return { device: 'mobile' };

  if (/Android/i.test(ua)) {
    return { device: /Mobile/i.test(ua) ? 'mobile' : 'tablet' };
  }

  if (isTouch && /Macintosh/i.test(ua)) {
    return { device: 'tablet' };
  }

  return { device: 'desktop' };
}

function parseBrowser(ua: string): BrowserInfo {
  const isEdge: boolean = /Edg\//.test(ua);
  const isOpera: boolean = /OPR\//.test(ua) || /Opera/.test(ua);
  const isBrave: boolean = /Brave\//.test(ua);
  const isFirefox: boolean = /Firefox\//.test(ua);
  const isSamsung: boolean = /SamsungBrowser\//.test(ua);
  const isUCBrowser: boolean = /UCBrowser\//.test(ua);
  const isChrome: boolean = /Chrome\//.test(ua) || /CriOS\//.test(ua);
  const isSafari: boolean = /Safari\//.test(ua) && !isChrome && !isEdge && !isOpera;

  if (isEdge) {
    return { browser: 'Edge', browserVersion: pick(ua, /Edg\/([0-9.]+)/) };
  }

  if (isOpera) {
    return {
      browser: 'Opera',
      browserVersion: pick(ua, /OPR\/([0-9.]+)/) || pick(ua, /Opera\/([0-9.]+)/),
    };
  }

  if (isSamsung) {
    return { browser: 'Samsung Internet', browserVersion: pick(ua, /SamsungBrowser\/([0-9.]+)/) };
  }

  if (isFirefox) {
    return { browser: 'Firefox', browserVersion: pick(ua, /Firefox\/([0-9.]+)/) };
  }

  if (isUCBrowser) {
    return { browser: 'UC Browser', browserVersion: pick(ua, /UCBrowser\/([0-9.]+)/) };
  }

  if (isChrome) {
    return {
      browser: isBrave ? 'Brave' : 'Chrome',
      browserVersion: pick(ua, /Chrome\/([0-9.]+)/) || pick(ua, /CriOS\/([0-9.]+)/),
    };
  }

  if (isSafari) {
    return { browser: 'Safari', browserVersion: pick(ua, /Version\/([0-9.]+)/) };
  }

  return { browser: 'unknown', browserVersion: 'unknown' };
}

function detectIOSWebView(ua: string): BrowserInfo | null {
  const isIOS: boolean = /iPhone|iPad|iPod/i.test(ua);
  const isWebView: boolean = isIOS && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);

  if (!isWebView) return null;

  return { browser: 'iOS WebView', browserVersion: 'unknown' };
}

function getOrCreateBrowserId(): string {
  try {
    const browserId: string | null = localStorage.getItem('browserId');
    if (browserId) return browserId;

    const id: string = uuid();
    localStorage.setItem('browserId', id);
    return id;
  } catch {
    return uuid();
  }
}
